/**
 * Common functions to support API calls.
 */
import axios from "axios";
import qs from "qs";

import store from "./store";

const api = {
  /**
   * Special GET method to handle keep-alive call made periodically by the V2 frontend.
   * This goes to the legacy back end so has a different response structure to most API queries.
   * @param {*} url
   */
  async keepAlive(url) {
    const response = await axios({ url, method: "get" }).catch((error) => {
      return {
        status: error.response ? error.response.status || "unknown" : "no response",
        statusText: error.response ? error.response.statusText || "unknown" : "no response",
        data: { loggedin: true }, // default to logged in to avoid undesirable redirects
      };
    });

    return {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
    };
  },

  /**
   * Make a GET request to an endpoint.
   * @param {String} url
   */
  async get(url) {
    return this._request("get", url);
  },

  /**
   * Fetch file.
   * TODO - I think this is a legacy method. Replace if necessary.
   * @param {String} url
   */
  async getFile(url) {
    const fileData = await axios({ url, method: "get", responseType: "blob" })
      .then((data) => {
        return data;
      })
      .catch((error) => {
        return error.response;
      });
    return fileData;
  },

  /**
   * Make multiple parallel GET requests to endpoints and wait for completion.
   * @param {Array} urls
   */
  async getMultiple(urls) {
    const queue = [];
    urls.forEach((url) => {
      queue.push(this.get(url));
    });
    const multiple = await Promise.all(queue);

    // TODO - error handling (or rely on the get() method to do it for us?).

    return multiple;
  },

  /**
   * Make a PATCH request to an endpoint.
   * @param {String} url
   * @param {Object} data
   */
  async patch(url, data) {
    return this._request("patch", url, data);
  },

  /**
   * Make a DELETE request to an endpoint.
   * @param {String} url
   * @param {Object} data
   */
  async delete(url, data) {
    return this._request("delete", url, data);
  },

  /**
   * Post JSON data to an endpoint.
   * @param {String} url
   * @param {Object} data
   */
  async post(url, data) {
    return this._request("post", url, data);
  },

  /**
   * Make a request which sends a CSRF header.
   * @param {String} method
   * @param {String} url
   * @param {Object} data
   */
  async _request(method, url, data) {
    // data._csrf = store.state.csrfToken; // Moved to header to avoid polluting POST data.

    // Configure axios request
    const axiosConfig = {
      url,
      method,
    };

    if (method !== "get") {
      // Support JSON and multi-part form data
      const headers = { "content-type": "application/json", "csrf-token": store.state.csrfToken };
      if (data && data instanceof FormData) {
        headers["content-type"] = "multipart/form-data";
      }

      axiosConfig.headers = headers;
      axiosConfig.data = data || {};
    }

    // Submit the request ...
    let responseObject = {};
    await axios(axiosConfig)
      .then((response) => {
        // Update the current CSRF token.
        store.commit("setCsrfToken", response.data.csrfToken);
        // Handle the response data.
        responseObject = {
          status: response.status,
          statusText: response.statusText,
          message: response.data.message,
          data: response.data.data,
        };
      })
      .catch((error) => {
        // Update the current CSRF token
        if (error.response.data.csrfToken) {
          store.commit("setCsrfToken", error.response.data.csrfToken);
        } else {
          // TODO - something went very wrong - fetch a new CSRF token from the API.
        }

        // Generate error response object
        // TODO - handle case where we have a complete breakdown and the following data doesn't exist in the response.
        responseObject = {
          status: error.response.status,
          statusText: error.response.statusText,
          message: error.response.data.message,
          data: error.response.data.data,
        };
      });

    // Handle unauthenticated user.
    if (responseObject.status === 401) {
      this.redirectToLogin();
    }

    return responseObject;
  },

  /**
   * Download a file from the specified URL.
   * @param {string} url
   */
  async download(url, filename) {
    let responseObject = {};
    await axios
      .get(url, { responseType: "blob" })
      .then((response) => {
        const blob = new Blob([response.data], { type: response.data.type });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);

        responseObject = {
          status: response.status,
          statusText: response.statusText,
        };
      })
      .catch((error) => {
        responseObject = {
          status: error.response.status,
          statusText: error.response.statusText,
        };
      });

    // Handle unauthenticated user.
    if (responseObject.status === 401) {
      this.redirectToLogin();
      return;
    }

    return responseObject;
  },

  /**
   * Redirect to the login page, typically after it has been detected that the user is not logged in.
   * Sets the current page as the page to be navigated to after logging in.
   */
  redirectToLogin() {
    let pathname = window.location.pathname;
    let nextpath = "";
    if (pathname.length) {
      pathname = pathname.substring(1);
      if (pathname.length) {
        nextpath = `?next=${pathname}`;
      }
    }
    window.location.href = `/login${nextpath}`;
  },

  // THE FOLLOWING ARE LEGACY CODEIGNITER API METHODS AND CAN BE REMOVED ONCE THE CURATION PAGES ARE REVISITED.

  /**
   * Post URL encoded data to an endpoint.
   * This is required for sending data to the legacy CodeIgniter API. CodeIgniter only accepting its CSRF hash in form
   * data, so we need to convert to form-urlencoded content type before sending.
   *
   * TODO - work out a way to send JSON
   * E.g. see note on modifying security library here:
   * https://stackoverflow.com/questions/18798898/codeigniter-csrf-token-in-json-request,
   * or replace CodeIgniter with something modern ... :(
   *
   * @param {String} url
   * @param {Object} data
   */
  async postUrlEncoded(url, data) {
    data.firm_academy_csrf_token = store.state.csrf_hash;
    const dataQs = qs.stringify(data);
    return axios({
      url,
      method: "post",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      data: dataQs,
    })
      .then((response) => {
        if (typeof response.data != "undefined" && typeof response.data.csrf_hash != "undefined") {
          store.commit("setCsrfHash", response.data.csrf_hash);
        } else {
          api.get("/api/getCsrfHash").then((recoveryResponse) => {
            store.commit("setCsrfHash", recoveryResponse.data.csrf_hash);
          });
        }
        return response;
      })
      .catch((reason) => {
        if (
          typeof reason.response != "undefined" &&
          typeof reason.response.data != "undefined" &&
          typeof reason.response.data.csrf_hash != "undefined"
        ) {
          store.commit("setCsrfHash", reason.response.data.csrf_hash);
        } else {
          api.get("/api/getCsrfHash").then((recoveryResponse) => {
            store.commit("setCsrfHash", recoveryResponse.data.csrf_hash);
          });
        }
        return reason;
      });
  },

  /**
   * Multipart POST for uploading files.
   * Note - in an ideal world we would do this via a PATCH. However as things stand CodeIgniter makes it difficult.
   *
   * @param {String} url
   * @param {Object} file
   */
  async postMultipart(url, file) {
    const formData = new FormData();
    formData.append("firm_academy_csrf_token", store.state.csrf_hash);
    formData.append("file", file);
    return axios({
      url,
      method: "post",
      headers: { "content-type": "multipart/form-data" },
      data: formData,
    })
      .then((response) => {
        store.commit("setCsrfHash", response.data.csrf_hash);
        return response;
      })
      .catch((reason) => {
        store.commit("setCsrfHash", reason.response.data.csrf_hash);
        return reason;
      });

    // TODO - handle case where something goes so wrong that we don't have a CSRF hash, plus any other standard errors.
  },
};

export default api;
